import {useCallback} from 'react';

import {useApolloClient} from '@apollo/react-hooks';

import type {MarketId} from 'nutshell-core/types';

import type {
    GetProductPickerSuggestions,
    GetProductPickerSuggestionsVariables,
    ProductPickerProductFragment,
} from 'nutshell-graphql-types';

import {toProductPickerProduct} from '../helpers/product-picker-product';
import type {ProductPickerProduct} from '../helpers/product-picker-product';
import GET_PRODUCT_PICKER_SUGGESTIONS from '../queries/get-product-picker-suggestions.graphql';

interface ProductPickerSuggestionOptions {
    marketId?: MarketId;
    quoteId?: string;
}

export function useGetProductPickerSuggestions({
    marketId,
    quoteId,
}: ProductPickerSuggestionOptions): {
    getProductPickerSuggestions: () => Promise<ProductPickerProduct[]>;
    getProductPickerSuggestionResults: () => Promise<ProductPickerProductFragment[]>;
} {
    const client = useApolloClient();

    const getProductPickerSuggestionResults = useCallback(async (): Promise<
        ProductPickerProductFragment[]
    > => {
        const {data} = await client.query<
            GetProductPickerSuggestions,
            GetProductPickerSuggestionsVariables
        >({
            query: GET_PRODUCT_PICKER_SUGGESTIONS,
            variables: {
                ...(marketId ? {marketId} : {}),
                ...(quoteId ? {quoteId} : {}),
            },
            fetchPolicy: 'network-only',
        });

        return data.productPickerSuggestions;
    }, [client, marketId, quoteId]);

    const getProductPickerSuggestions = useCallback(async (): Promise<ProductPickerProduct[]> => {
        const results = await getProductPickerSuggestionResults();

        return results.map((result) => toProductPickerProduct(result, marketId));
    }, [getProductPickerSuggestionResults, marketId]);

    return {getProductPickerSuggestions, getProductPickerSuggestionResults};
}
